import React, { useEffect } from 'react';
import { useT } from '../i18n/LanguageContext';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  Zap,
  Sun,
  Building2,
  BatteryCharging,
  Ruler,
  TrendingUp,
  Award,
  Phone,
} from 'lucide-react';

const heroBg =
  'https://images.unsplash.com/photo-1509391366360-2e959784a276?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHBhbmVsc3xlbnwwfHx8fDE3ODQ4MTY4NDJ8MA&ixlib=rb-4.1.0&q=85';

const content = {
  fr: {
    breadcrumb: 'Accueil',

    heroTitle: 'À propos',
    heroAccent: 'de nous',
    heroSubtitle:
      'Votre partenaire de confiance en énergies renouvelables et en infrastructures électriques.',

    companyBadge: '+ NOTRE ENTREPRISE',
    companyTitle: 'Experts en énergie',
    companyAccent: 'depuis plus d’une décennie',
    companyText:
      'GPE – ÉNERGIES & SERVICES est une entreprise spécialisée dans un large éventail de domaines, offrant des solutions d’avenir pour un approvisionnement énergétique durable.',

    bullets: [
      'Installations photovoltaïques et énergies renouvelables',
      'Travaux électriques industriels et tertiaires',
      'Infrastructure de recharge pour véhicules électriques',
    ],

    companyText2:
      'Nous accompagnons nos clients avec des solutions techniques et économiques fiables, conçues pour répondre aux enjeux énergétiques d’aujourd’hui et de demain.',

    contactButton: 'Contactez-nous',

    missionTitle: 'Notre mission',
    missionText:
      'Accompagner nos clients dans leur transition énergétique avec des solutions techniques fiables et sur mesure – de la conception à la mise en service.',

    years: 'Années d’expérience',
    partners: 'Partenaires',

    presence: 'Présence nationale',
    locations: 'Vitrolles · Paris · Lyon · Marseille',

    figuresBadge: '+ CHIFFRES CLÉS',
    figuresTitle: 'Des résultats qui',
    figuresAccent: 'parlent',

    figures: [
      {
        icon: Sun,
        value: '150+',
        label: 'Clients satisfaits',
        desc:
          'Des professionnels et des particuliers qui nous font confiance dans toute la France.',
      },
      {
        icon: Building2,
        value: '500+',
        label: 'Projets réalisés',
        desc:
          'Plus de 500 projets livrés avec succès dans le tertiaire, l’industrie et le résidentiel.',
      },
      {
        icon: TrendingUp,
        value: 'Croissance',
        label: 'Maîtrisée et durable',
        desc:
          'Une croissance structurée qui reflète la confiance renouvelée de nos clients.',
      },
    ],

    capabilitiesBadge: '+ ORGANISATION & RESSOURCES',
    capabilitiesTitle: 'Nos capacités &',
    capabilitiesAccent: 'expertise',
    capabilitiesSubtitle:
      'Des ressources humaines et techniques dédiées à l’excellence sur chacun de vos projets.',

    capabilities: [
      {
        icon: Ruler,
        title: "Bureau d'études intégré",
        desc:
          "Conception, dimensionnement et suivi technique des projets, de l’étude initiale à la mise en service.",
      },
      {
        icon: Award,
        title: 'Équipes techniques certifiées',
        desc:
          'Suivi opérationnel de vos chantiers assuré par des équipes qualifiées et certifiées.',
      },
      {
        icon: Sun,
        title: 'Équipes photovoltaïques',
        desc:
          'Installateurs spécialisés en photovoltaïque pour des déploiements rapides et conformes RGE.',
      },
      {
        icon: Zap,
        title: 'Équipes électriques',
        desc:
          'Spécialistes des travaux électriques commerciaux et industriels sur réseaux BT et HT.',
      },
      {
        icon: BatteryCharging,
        title: 'Équipes IRVE',
        desc:
          'Équipes IRVE certifiées dédiées aux infrastructures de recharge pour véhicules électriques.',
      },
    ],

    ctaTitle: 'Vous avez un projet',
    ctaAccent: 'en tête',
    ctaText:
      'Notre équipe est prête à étudier votre projet et à vous proposer une solution sur mesure, de la conception à la mise en service.',
    quoteButton: 'Demander un devis',
  },

  en: {
    breadcrumb: 'Home',

    heroTitle: 'About',
    heroAccent: 'us',
    heroSubtitle:
      'Your trusted partner in renewable energy and electrical infrastructure.',

    companyBadge: '+ OUR COMPANY',
    companyTitle: 'Energy experts',
    companyAccent: 'for more than a decade',
    companyText:
      'GPE – ENERGIES & SERVICES is a company specialised in a wide range of fields, offering forward-looking solutions for a sustainable energy supply.',

    bullets: [
      'Photovoltaic installations and renewable energy',
      'Industrial and commercial electrical works',
      'Electric vehicle charging infrastructure',
    ],

    companyText2:
      'We support our clients with reliable technical and cost-effective solutions designed to meet today’s and tomorrow’s energy challenges.',

    contactButton: 'Contact us',

    missionTitle: 'Our mission',
    missionText:
      'Supporting our clients in their energy transition with reliable, tailor-made technical solutions – from design to commissioning.',

    years: 'Years of experience',
    partners: 'Partners',

    presence: 'National presence',
    locations: 'Vitrolles · Paris · Lyon · Marseille',

    figuresBadge: '+ KEY FIGURES',
    figuresTitle: 'Results that',
    figuresAccent: 'speak',

    figures: [
      {
        icon: Sun,
        value: '150+',
        label: 'Satisfied clients',
        desc:
          'Professionals and individuals who trust us throughout France.',
      },
      {
        icon: Building2,
        value: '500+',
        label: 'Projects completed',
        desc:
          'More than 500 successfully delivered projects across commercial, industrial and residential sectors.',
      },
      {
        icon: TrendingUp,
        value: 'Growth',
        label: 'Controlled and sustainable',
        desc:
          'Structured growth reflecting the renewed trust of our clients.',
      },
    ],

    capabilitiesBadge: '+ ORGANISATION & RESOURCES',
    capabilitiesTitle: 'Our capabilities &',
    capabilitiesAccent: 'expertise',
    capabilitiesSubtitle:
      'Human and technical resources dedicated to excellence on every one of your projects.',

    capabilities: [
      {
        icon: Ruler,
        title: 'Integrated engineering office',
        desc:
          'Project design, sizing and technical monitoring from initial study through commissioning.',
      },
      {
        icon: Award,
        title: 'Certified technical teams',
        desc:
          'Operational monitoring of your projects by qualified and certified teams.',
      },
      {
        icon: Sun,
        title: 'Photovoltaic teams',
        desc:
          'Specialised photovoltaic installers for fast and compliant RGE deployments.',
      },
      {
        icon: Zap,
        title: 'Electrical teams',
        desc:
          'Specialists in commercial and industrial electrical works on LV and MV networks.',
      },
      {
        icon: BatteryCharging,
        title: 'EV charging teams',
        desc:
          'Certified EV charging teams dedicated to electric vehicle charging infrastructure.',
      },
    ],

    ctaTitle: 'Have a project',
    ctaAccent: 'in mind',
    ctaText:
      'Our team is ready to study your project and propose a tailored solution, from design to commissioning.',
    quoteButton: 'Request a quote',
  },

  ar: {
    breadcrumb: 'الرئيسية',

    heroTitle: 'من',
    heroAccent: 'نحن',
    heroSubtitle:
      'شريككم الموثوق في مجال الطاقات المتجددة والبنية التحتية الكهربائية.',

    companyBadge: '+ شركتنا',
    companyTitle: 'خبراء في الطاقة',
    companyAccent: 'منذ أكثر من عقد',
    companyText:
      'GPE – ENERGIES & SERVICES هي شركة متخصصة في مجموعة واسعة من المجالات، تقدم حلولاً مستقبلية لتوفير طاقة مستدامة.',

    bullets: [
      'التركيبات الكهروضوئية والطاقات المتجددة',
      'الأعمال الكهربائية الصناعية والتجارية',
      'البنية التحتية لشحن السيارات الكهربائية',
    ],

    companyText2:
      'نرافق عملاءنا من خلال حلول تقنية واقتصادية موثوقة مصممة لتلبية تحديات الطاقة الحالية والمستقبلية.',

    contactButton: 'اتصل بنا',

    missionTitle: 'مهمتنا',
    missionText:
      'مساعدة عملائنا في التحول نحو الطاقة المستدامة من خلال حلول تقنية موثوقة ومخصصة، من التصميم إلى التشغيل.',

    years: 'سنوات من الخبرة',
    partners: 'شركاء',

    presence: 'حضور وطني',
    locations: 'فيترول · باريس · ليون · مرسيليا',

    figuresBadge: '+ أرقام رئيسية',
    figuresTitle: 'نتائج',
    figuresAccent: 'تتحدث عن نفسها',

    figures: [
      {
        icon: Sun,
        value: '150+',
        label: 'عملاء راضون',
        desc:
          'محترفون وأفراد يثقون بنا في جميع أنحاء فرنسا.',
      },
      {
        icon: Building2,
        value: '500+',
        label: 'مشاريع منجزة',
        desc:
          'أكثر من 500 مشروع تم إنجازه بنجاح في القطاعات التجارية والصناعية والسكنية.',
      },
      {
        icon: TrendingUp,
        value: 'نمو',
        label: 'مدروس ومستدام',
        desc:
          'نمو منظم يعكس ثقة عملائنا المتجددة.',
      },
    ],

    capabilitiesBadge: '+ التنظيم والموارد',
    capabilitiesTitle: 'قدراتنا و',
    capabilitiesAccent: 'خبراتنا',
    capabilitiesSubtitle:
      'موارد بشرية وتقنية مخصصة لتحقيق التميز في كل مشروع من مشاريعكم.',

    capabilities: [
      {
        icon: Ruler,
        title: 'مكتب دراسات هندسية متكامل',
        desc:
          'تصميم المشاريع وحساب الأحجام والمتابعة الفنية من الدراسة الأولية حتى التشغيل.',
      },
      {
        icon: Award,
        title: 'فرق فنية معتمدة',
        desc:
          'متابعة تنفيذ مشاريعكم من خلال فرق مؤهلة ومعتمدة.',
      },
      {
        icon: Sun,
        title: 'فرق متخصصة في الطاقة الشمسية',
        desc:
          'فرق متخصصة في تركيب الأنظمة الكهروضوئية لتنفيذ سريع ومتوافق مع المعايير.',
      },
      {
        icon: Zap,
        title: 'فرق كهربائية',
        desc:
          'متخصصون في الأعمال الكهربائية التجارية والصناعية على شبكات الجهد المنخفض والمتوسط.',
      },
      {
        icon: BatteryCharging,
        title: 'فرق شحن السيارات الكهربائية',
        desc:
          'فرق معتمدة متخصصة في البنية التحتية لشحن السيارات الكهربائية.',
      },
    ],

    ctaTitle: 'لديكم مشروع',
    ctaAccent: 'في ذهنكم',
    ctaText:
      'فريقنا مستعد لدراسة مشروعكم واقتراح حل مخصص لكم، من التصميم إلى التشغيل.',
    quoteButton: 'طلب عرض سعر',
  },
};

const About = () => {
  const { lang } = useT();
  const c = content[lang] || content.fr;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const isArabic = lang === 'ar';

  return (
    <main className="bg-white" dir={isArabic ? 'rtl' : 'ltr'}>

      {/* HERO */}
      <section className="relative pt-40 pb-28 md:pt-48 md:pb-32 overflow-hidden">
        <img
          src={heroBg}
          alt={`${c.heroTitle} ${c.heroAccent}`}
          loading="eager"
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-gradient-to-b from-[#0b1c3a]/95 via-[#0b1c3a]/85 to-[#0b1c3a]/95" />

        <div className="absolute right-[-10%] top-1/2 -translate-y-1/2 w-[720px] h-[720px] rounded-full bg-[#0b3b8c] blur-[140px] opacity-40 pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">

          <nav
            aria-label="breadcrumb"
            className="flex items-center gap-2 text-white/70 text-sm"
          >
            <Link
              to="/"
              className="hover:text-white transition-colors"
            >
              &larr; {c.breadcrumb}
            </Link>
          </nav>

          <span className="inline-block mt-8 px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white/90 text-[11px] tracking-[0.25em] font-semibold">
            GPE · ÉNERGIES &amp; SERVICES
          </span>

      <h1 className="mt-6 text-white font-black leading-[1] tracking-tight text-5xl md:text-6xl lg:text-7xl">
  {c.heroTitle}{' '}
  <span className="font-serif italic text-[#f5c518]">
    {c.heroAccent}
  </span>
</h1>

          <p className="mt-6 text-white/85 text-base md:text-lg max-w-2xl leading-relaxed">
            {c.heroSubtitle}
          </p>
        </div>

        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-white" />
      </section>

      {/* INTRO + MISSION */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">

          {/* COMPANY */}
          <div>
            <span className="inline-block px-3 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-600 text-[11px] tracking-[0.25em] font-semibold">
              {c.companyBadge}
            </span>

            <h2 className="mt-6 text-4xl md:text-5xl font-black text-[#0b1c3a] leading-[1.05] tracking-tight">
              {c.companyTitle}{' '}
              <span className="italic font-serif text-[#0b3b8c]">
                {c.companyAccent}
              </span>
              .
            </h2>

            <p className="mt-6 text-slate-600 leading-relaxed">
              {c.companyText}
            </p>

            <ul className="mt-8 space-y-3">
              {c.bullets.map((bullet) => (
                <li
                  key={bullet}
                  className="flex items-start gap-3"
                >
                  <span className="mt-0.5 w-6 h-6 rounded-full bg-[#0b3b8c]/10 text-[#0b3b8c] flex items-center justify-center shrink-0">
                    <Check
                      className="w-3.5 h-3.5"
                      strokeWidth={3}
                    />
                  </span>

                  <span className="text-slate-700 text-[15px]">
                    {bullet}
                  </span>
                </li>
              ))}
            </ul>

            <p className="mt-6 text-slate-600 leading-relaxed">
              {c.companyText2}
            </p>

            <Link
              to="/contact"
              className="inline-flex mt-8 px-6 py-3 rounded-full bg-[#0b3b8c] text-white font-semibold text-sm hover:bg-[#082e6f] transition-colors shadow-md shadow-blue-900/20"
            >
              {c.contactButton}
            </Link>
          </div>

          {/* MISSION CARD */}
          <div className="relative lg:mt-16">

            <div className="relative bg-[#0b1c3a] text-white rounded-3xl p-8 md:p-10 shadow-2xl shadow-blue-900/20 overflow-hidden">

              <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#0b3b8c] blur-[70px] opacity-50" />

              <div className="absolute -bottom-24 -left-16 w-64 h-64 rounded-full bg-[#f5c518] blur-[90px] opacity-15" />

              <div className="relative">

                <div className="w-12 h-12 rounded-2xl bg-[#f5c518] text-[#0b1c3a] flex items-center justify-center">
                  <Zap
                    className="w-6 h-6"
                    strokeWidth={2.5}
                  />
                </div>

                <h3 className="mt-6 text-2xl md:text-3xl font-black tracking-tight">
                  {c.missionTitle}
                </h3>

                <p className="mt-4 text-white/80 text-[15px] leading-relaxed">
                  {c.missionText}
                </p>

                <div className="mt-8 pt-8 border-t border-white/10 grid grid-cols-2 gap-6">

                  <div>
                    <div className="text-[#f5c518] text-3xl md:text-4xl font-black tracking-tight">
                      10+
                    </div>

                    <div className="mt-1 text-white/60 text-[11px] tracking-[0.2em] font-semibold uppercase">
                      {c.years}
                    </div>
                  </div>

                  <div>
                    <div className="text-[#f5c518] text-3xl md:text-4xl font-black tracking-tight">
                      +50
                    </div>

                    <div className="mt-1 text-white/60 text-[11px] tracking-[0.2em] font-semibold uppercase">
                      {c.partners}
                    </div>
                  </div>

                </div>
              </div>
            </div>

            {/* PRESENCE */}
            <div className="hidden md:flex absolute -bottom-6 left-8 right-8 lg:left-16 items-center gap-3 bg-white rounded-2xl shadow-xl border border-slate-100 px-5 py-3">

              <div className="w-8 h-8 rounded-full bg-[#0b3b8c]/10 text-[#0b3b8c] flex items-center justify-center">
                <Building2 className="w-4 h-4" />
              </div>

              <div>
                <div className="text-[#0b1c3a] text-sm font-bold">
                  {c.presence}
                </div>

                <div className="text-slate-500 text-xs">
                  {c.locations}
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* KEY FIGURES */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">

          <div className="text-center">

            <span className="inline-block px-3 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-600 text-[11px] tracking-[0.25em] font-semibold">
              {c.figuresBadge}
            </span>

            <h2 className="mt-6 text-4xl md:text-5xl font-black text-[#0b1c3a] leading-[1.05] tracking-tight">
              {c.figuresTitle}{' '}
              <span className="italic font-serif text-[#0b3b8c]">
                {c.figuresAccent}
              </span>
              .
            </h2>

          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-6">

            {c.figures.map((figure) => {
              const Icon = figure.icon;

              return (
                <div
                  key={figure.label}
                  className="group relative bg-white rounded-3xl p-8 md:p-10 border border-slate-200 hover:border-[#0b3b8c]/30 hover:shadow-2xl transition-all duration-300"
                >

                  <div className="w-14 h-14 rounded-2xl bg-[#0b3b8c]/8 text-[#0b3b8c] flex items-center justify-center group-hover:bg-[#0b3b8c] group-hover:text-white transition-colors">
                    <Icon
                      className="w-6 h-6"
                      strokeWidth={2.2}
                    />
                  </div>

                  <div className="mt-8 text-[#0b1c3a] text-4xl md:text-5xl font-black tracking-tight">
                    {figure.value}
                  </div>

                  <div className="mt-2 text-[#0b3b8c] font-bold text-sm">
                    {figure.label}
                  </div>

                  <div className="mt-5 h-[3px] w-14 bg-[#f5c518] rounded-full" />

                  <p className="mt-4 text-slate-500 text-sm leading-relaxed">
                    {figure.desc}
                  </p>

                </div>
              );
            })}

          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="py-24 bg-[#f7f8fb] border-y border-slate-200">

        <div className="max-w-7xl mx-auto px-6 lg:px-10">

          <div className="text-center max-w-3xl mx-auto">

            <span className="inline-block px-3 py-1 rounded-full border border-slate-200 bg-white text-slate-600 text-[11px] tracking-[0.25em] font-semibold">
              {c.capabilitiesBadge}
            </span>

            <h2 className="mt-6 text-4xl md:text-5xl font-black text-[#0b1c3a] leading-[1.05] tracking-tight">
              {c.capabilitiesTitle}{' '}
              <span className="italic font-serif text-[#0b3b8c]">
                {c.capabilitiesAccent}
              </span>
              .
            </h2>

            <p className="mt-4 text-slate-600">
              {c.capabilitiesSubtitle}
            </p>

          </div>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">

            {c.capabilities.map((capability) => {
              const Icon = capability.icon;

              return (
                <div
                  key={capability.title}
                  className="group bg-white rounded-2xl border border-slate-200 p-7 hover:shadow-xl hover:-translate-y-1 hover:border-[#0b3b8c]/30 transition-all duration-300"
                >

                  <div className="w-11 h-11 rounded-xl bg-[#f5c518]/15 text-[#c19a00] flex items-center justify-center group-hover:bg-[#0b3b8c] group-hover:text-white transition-colors">
                    <Icon
                      className="w-5 h-5"
                      strokeWidth={2.2}
                    />
                  </div>

                  <h3 className="mt-5 text-[#0b1c3a] font-bold text-lg leading-snug">
                    {capability.title}
                  </h3>

                  <p className="mt-2 text-slate-600 text-sm leading-relaxed">
                    {capability.desc}
                  </p>

                </div>
              );
            })}

          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">

        <div className="max-w-7xl mx-auto px-6 lg:px-10">

          <div className="relative rounded-3xl overflow-hidden bg-[#0b1c3a] px-8 py-16 md:px-16 md:py-20 text-white text-center">

            <div className="absolute inset-0 opacity-25 bg-[radial-gradient(circle_at_20%_50%,#0b3b8c_0%,transparent_45%),radial-gradient(circle_at_80%_50%,#f5c518_0%,transparent_35%)]" />

            <div className="relative">

              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tight leading-tight">
                {c.ctaTitle}{' '}
                <span className="italic font-serif text-[#f5c518]">
                  {c.ctaAccent}
                </span>
                ?
              </h2>

              <p className="mt-4 text-white/80 max-w-2xl mx-auto">
                {c.ctaText}
              </p>

              <div className="mt-8 flex flex-wrap justify-center gap-3">

                <Link
                  to="/contact"
                  className="px-6 py-3 rounded-full bg-[#f5c518] text-[#0b1c3a] font-bold text-sm hover:bg-yellow-300 transition-colors inline-flex items-center gap-2"
                >
                  {c.quoteButton}
                  <ArrowRight className="w-4 h-4" />
                </Link>

                <a
                  href="tel:+33442072262"
                  className="px-6 py-3 rounded-full border border-white/25 text-white font-semibold text-sm hover:bg-white/10 transition-colors inline-flex items-center gap-2"
                >
                  <Phone className="w-4 h-4" />
                  +33 4 42 07 22 62
                </a>

              </div>
            </div>
          </div>
        </div>
      </section>

    </main>
  );
};

export default About;